import {
  BaseEditor,
  type EditorFeatureSet,
  type EditorProseSize,
} from './BaseEditor';
import { MarkdownExtension } from './extensions';
import { useMountNormalizationGuard } from './useMountNormalizationGuard';

import type { Editor } from '@tiptap/core';

export interface MarkdownValueEditorProps {
  id: string;
  /** Initial markdown content to display in the editor */
  value: string;
  /** Callback fired when editor content changes, receives a markdown string */
  onChange: (markdown: string) => void;
  ariaLabel?: string;
  placeholder?: string;
  /** Which editor feature set to expose. @default 'full' */
  featureSet?: EditorFeatureSet;
  /** Contenteditable typography density. @default 'base' */
  proseSize?: EditorProseSize;
  className?: string;
  /** When true, focus the editor on mount. @default false */
  autoFocus?: boolean;
  /** Extension names to exclude from the default set */
  excludeExtensions?: string[];
}

const getMarkdown = (editor: Editor) =>
  (
    editor.storage as unknown as { markdown: { getMarkdown(): string } }
  ).markdown.getMarkdown();

export const MarkdownValueEditor = ({
  id,
  value,
  onChange,
  ariaLabel,
  placeholder,
  featureSet,
  proseSize,
  className,
  autoFocus = false,
  excludeExtensions,
}: MarkdownValueEditorProps) => {
  const { onCreate, onUpdate } = useMountNormalizationGuard(
    getMarkdown,
    onChange
  );

  return (
    <BaseEditor<string>
      id={id}
      initialContent={value}
      onCreate={onCreate}
      onUpdate={onUpdate}
      additionalExtensions={[MarkdownExtension]}
      ariaLabel={ariaLabel}
      placeholder={placeholder}
      featureSet={featureSet}
      proseSize={proseSize}
      className={className}
      autofocus={autoFocus}
      excludeExtensions={excludeExtensions}
    />
  );
};
